import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldAlert, HelpCircle, Code2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { toolBySlug, COLOR } from "../lib/tools";
import { CALCULATORS, ToolSlugContext } from "../components/tools/calculators";
import { SavedNumbers } from "../components/tools/SavedNumbers";
import { useToolsT } from "../lib/toolsI18n";
import { toolSeo } from "../lib/toolsSeo";
import { Seo, SITE } from "../lib/seo";
import { useLang } from "../lib/i18n";

function EmbedBlock({ slug, title }) {
  const { tt } = useToolsT();
  const [copied, setCopied] = useState(false);
  const code = `<iframe id="amd-${slug}" src="${SITE}/embed/tools/${slug}" title="${title} — AskMyDoctor AI" width="100%" height="560" style="max-width:520px;border:1px solid #e2e8f0;border-radius:16px" loading="lazy"></iframe>
<script>window.addEventListener("message",function(e){if(e.origin==="${SITE}"&&e.data&&e.data.type==="amd-embed-height"&&e.data.slug==="${slug}"){var f=document.getElementById("amd-${slug}");if(f){f.style.height=e.data.height+"px";}}});</script>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Fallback for browsers/iframes without clipboard-write permission.
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
    }
    setCopied(true);
    toast.success(tt("embedCopied"));
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-5 rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid="embed-block">
      <h2 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
        <Code2 className="w-5 h-5 text-teal-600" /> {tt("embedTitle")}
      </h2>
      <p className="text-sm text-slate-500 mt-1 mb-3">{tt("embedDesc")}</p>
      <textarea
        readOnly
        value={code}
        data-testid="embed-code"
        onFocus={(e) => e.target.select()}
        rows={5}
        className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-3.5 py-3 text-xs text-slate-700 font-mono resize-none"
      />
      <button
        onClick={copy}
        data-testid="embed-copy"
        className="mt-3 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm px-4 py-2 rounded-xl transition-transform active:scale-95"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? tt("embedCopied") : tt("embedCopy")}
      </button>
    </div>
  );
}

export default function ToolDetailPage() {
  const { slug } = useParams();
  const { tt, reg } = useToolsT();
  const { lang } = useLang();
  const tool = toolBySlug(slug);
  const Calc = CALCULATORS[slug];

  if (!tool || !Calc) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold text-slate-900">{tt("notFound")}</h1>
        <Link to="/tools" className="inline-flex items-center gap-1 text-teal-600 font-semibold mt-4">
          <ArrowLeft className="w-4 h-4" /> {tt("all")}
        </Link>
      </div>
    );
  }

  const c = COLOR[tool.color];
  const Icon = tool.icon;
  const meta = reg(slug);
  const seo = toolSeo(slug, lang);
  const url = `${SITE}/tools/${slug}`;
  const ogImage = `${SITE}/og-tools-${slug}.jpg`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: meta.t,
      url,
      description: seo.desc,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Free Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 2, name: meta.t, item: url },
      ],
    },
  ];
  if (seo.faq.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: seo.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={`tool-detail-${slug}`}>
      <Seo title={`${meta.t} | AskMyDoctor AI`} description={seo.desc} path={`/tools/${slug}`} image={ogImage} jsonLd={jsonLd} />

      <Link to="/tools" data-testid="tools-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-teal-700 transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> {tt("all")}
      </Link>

      <div className="flex items-start gap-4 mb-8">
        <div className={`inline-grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br ${c.grad} text-white shadow-sm flex-shrink-0`}>
          <Icon className="w-7 h-7" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{meta.t}</h1>
          <p className="text-slate-600 mt-1">{meta.d}</p>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-slate-200 bg-white p-6 sm:p-7">
        <ToolSlugContext.Provider value={slug}>
          <Calc />
        </ToolSlugContext.Provider>
      </div>

      <SavedNumbers slug={slug} />

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-amber-50 border-2 border-amber-200 p-4" data-testid="tool-disclaimer">
        <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 leading-relaxed">{tt("toolDisclaimer")}</p>
      </div>

      {seo.faq.length > 0 && (
        <div className="mt-8" data-testid="tool-faq">
          <h2 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-teal-600" /> {tt("faqTitle")}
          </h2>
          <div className="space-y-3">
            {seo.faq.map((f, i) => (
              <div key={i} className="rounded-2xl border-2 border-slate-200 bg-white p-5">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="text-slate-600 mt-1.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <EmbedBlock slug={slug} title={meta.t} />
    </div>
  );
}
